import Vue from 'vue'

import { EventService } from 'base/services/EventService'

import { Dictionary } from 'base/store/state.interface'
import { RowIndicatorDetails } from '.'
import { Widget, WidgetRow, WidgetType } from '..'
import { store } from '../../../../base/store'
import WidgetContainerFactory from '../../container/WidgetContainerFactory'
import { WidgetContainer } from '../../container/WidgetContainerInterface'
import { ResizeService } from '../resize'
import { WidgetRowFactory } from '../row/WidgetRowFactory'
import WidgetFactory from '../widget/WidgetFactory'
import {
  WidgetSetting,
  WidgetSettingsCheckbox,
  WidgetSettingsCheckboxList,
  WidgetSettingsDetails,
  WidgetSettingType
} from '../widget/WidgetInterface'
import { WidgetService } from '../widget/WidgetService'
import { WidgetHeightType } from '../widget/WidgetTypes'
import { ShrinkProposalList } from './PermutatorTypes'

class PermutatorServiceProvider {
  protected pristineData: WidgetContainer
  protected container: WidgetContainer | null
  protected rootContainerUUID: string
  protected rowContainingPlaceholderUUID: string
  protected rowDataBeforeShrinkOperation: Object = {}

  errorState: string = ''

  uuid(): string {
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1)
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
  }

  protected async executeWidgetDataInitializer(widget: Widget) {
    if (!widget.dataInitializer) return

    void store.dispatch(widget.dataInitializer)
  }

  protected syncWidgetCustomSettingField(targetField: WidgetSetting, sourceField: WidgetSetting) {
    const protectedFields: string[] = []

    switch (sourceField.type) {
    case WidgetSettingType.selectbox:
    case WidgetSettingType.text:
    case WidgetSettingType.number:
      protectedFields.push('value')
      break
    case WidgetSettingType.checkbox:
      protectedFields.push('checked')
      break
    case WidgetSettingType.checkboxList:
    case WidgetSettingType.sortableList:
    case WidgetSettingType.sortableCheckboxList:
      protectedFields.push('list', 'values')

      const cbSourceField = sourceField as WidgetSettingsCheckboxList
      const values: Array<string | number> = []
      const newList: Dictionary<WidgetSettingsCheckbox> | any = []
      const list = cbSourceField.list
      const cbTargetField = targetField as WidgetSettingsCheckboxList

      for (const cboxname in list) {
        if (!list.hasOwnProperty(cboxname)) {
          continue
        }

        const cboxSrc = cbSourceField.list[cboxname]

        if (cbTargetField.list[cboxname]) {
          const cboxTgt = cbTargetField.list[cboxname]

          // if the checkbox is checked in the stored data, we take the specification array's value
          // since that value is the most recent one, while the stored data's value might be outdated
          if (cboxTgt.checked) {
            values.push(cboxSrc.value || cboxname)
          }

          // Here we need a DeepCopy of the Checkbox settings element,
          // else we just pass a reference that is `false` outside of the condition scope
          newList.push(JSON.parse(JSON.stringify(cboxTgt)))

          this.syncWidgetCustomSettingField(cbTargetField.list[cboxname], cboxSrc)

          continue
        }

        newList.push(cbTargetField.list[cboxname])
      }

      cbTargetField.values = values
      cbTargetField.list = newList
      break
    case WidgetSettingType.custom:
      protectedFields.push('data')
      break
    default:
      // do nothing
      break
    }

    for (const prop in sourceField) {
      if (!sourceField.hasOwnProperty(prop) || (targetField.hasOwnProperty(prop) && (protectedFields.indexOf(prop) > -1))) {
        continue
      }

      Vue.set(targetField, prop, sourceField[prop])
    }
  }

  protected syncWidgetCustomSettings(targetSettings: WidgetSettingsDetails, sourceSettings: WidgetSettingsDetails) {
    if (sourceSettings.sections) {
      Vue.set(targetSettings, 'sections', JSON.parse(JSON.stringify(sourceSettings.sections)))
    }

    const targetFields = targetSettings.settings
    const sourceFields = sourceSettings.settings

    for (const settingName in sourceFields) {
      if (!sourceFields.hasOwnProperty(settingName)) {
        continue
      }

      const setting = sourceFields[settingName]

      if (!targetFields[settingName]) {
        Vue.set(targetFields, settingName, setting)
        continue
      }

      this.syncWidgetCustomSettingField(targetFields[settingName], setting)
    }
  }

  protected pruneWidgetCustomSettings(widget: any, spec: any) {
    for (const prop in widget) {
      if (!widget.hasOwnProperty(prop) || (prop === 'data')) {
        continue
      }

      if (!spec.hasOwnProperty(prop)) {
        delete widget[prop]
        continue
      }

      const val = widget[prop]
      const specVal = spec[prop]

      if (
        ((typeof val !== 'object') || Array.isArray(val)) ||
        ((typeof specVal !== 'object') || Array.isArray(specVal))
      ) {
        continue
      }

      this.pruneWidgetCustomSettings(val, specVal)
    }
  }

  protected isProtectedWidgetProp(propName: string) {
    const protectedProps = ['title', 'size', 'height', 'rows', 'sortindex', 'customSettings']
    return protectedProps.indexOf(propName) !== -1
  }

  protected initialiseWidget(widget: Widget) {
    const specs = WidgetService.getWidgetSpecification(widget.type)

    // make sure widgets have all required properties to avoid errors in data handling
    for (const prop in specs) {
      if (!specs.hasOwnProperty(prop) || (widget.hasOwnProperty(prop) && this.isProtectedWidgetProp(prop))) {
        continue
      }
      Vue.set(widget, prop, specs[prop])
    }

    // custom settings are more complex to sync
    if (specs.customSettings) {
      if (widget.customSettings) {
        this.syncWidgetCustomSettings(widget.customSettings, specs.customSettings)
        // remove old settings that are no longer part of the specification
        this.pruneWidgetCustomSettings(widget.customSettings, specs.customSettings)
      } else {
        Vue.set(widget, 'customSettings', specs.customSettings)
      }
    }

    if (!widget.size) {
      Vue.set(widget, 'size', specs.defaultSize)
    }

    if (!widget.heightType) {
      widget.heightType = widget.defaultHeightType || WidgetHeightType.automatic
    }

    if (!widget.height) {
      widget.height = widget.defaultHeight || 0
    }
  }

  protected initialiseData(rows: WidgetRow[]) {
    const numRows = rows.length
    let rowIndex = 0
    for (; rowIndex < numRows; rowIndex++) {
      const row = rows[rowIndex]
      Vue.set(row, 'wbUUID', this.uuid())

      const widgets = row.widgets
      const numWidgets = widgets.length
      let i = 0
      for (; i < numWidgets; i++) {
        const widget = widgets[i]
        Vue.set(widget, 'wbUUID', this.uuid())

        this.initialiseWidget(widget)
        void this.executeWidgetDataInitializer(widget)

        if (widget.rows) {
          this.initialiseData(widget.rows)
        }
      }
    }
  }

  protected _getByUUID(rows: WidgetRow[], uuid: string): WidgetRow | Widget | null {
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      if (row.wbUUID === uuid) {
        return row
      }

      for (let widgetIndex = 0; widgetIndex < row.widgets.length; widgetIndex++) {
        const widget = row.widgets[widgetIndex]
        if (widget.wbUUID === uuid) {
          return widget
        }

        if (widget.rows) {
          const result = this._getByUUID(widget.rows, uuid)
          if (result) {
            return result
          }
        }
      }
    }

    return null
  }

  protected extractIndicatorFromRow(row: WidgetRow): RowIndicatorDetails {
    const index = row.widgets.findIndex((widget) => widget.type === WidgetType.dragDropIndicator)
    if (index !== -1) {
      const widget = row.widgets[index]
      return { widget, index }
    }

    return { widget: null, index: -1 }
  }

  getRowContainer(rowUUID: string, root?: WidgetContainer): WidgetContainer | null {
    root = root || this.container!

    let rows = root.rows
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      let row = rows[rowIndex]
      if (row.wbUUID === rowUUID) return root

      for (let widgetIndex = 0; widgetIndex < row.widgets.length; widgetIndex++) {
        const widget = row.widgets[widgetIndex]
        if (!widget.rows) continue

        const result = this.getRowContainer(rowUUID, widget as WidgetContainer)
        if (result !== null) return result
      }
    }

    return null
  }

  getParentUUIDs(widgetUUID: string, root?: WidgetContainer, rootPath?: string[]): string[] {
    root = root || this.getContainer()
    rootPath = rootPath || [root.wbUUID!]

    const rows = root.rows
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      const rowPath = [...rootPath, row.wbUUID!]

      const numWidgets = row.widgets.length

      for (let widgetIndex = 0; widgetIndex < numWidgets; widgetIndex++) {
        const widget = row.widgets[widgetIndex]
        const widgetPath = [...rowPath, widget.wbUUID!]

        if (widget.wbUUID === widgetUUID) {
          return widgetPath
        }

        if (!widget.rows) {
          continue
        }

        const result = this.getParentUUIDs(widgetUUID, widget as WidgetContainer, widgetPath)

        if (result.length > 0) {
          return result
        }
      }
    }

    return []
  }

  getNestedChildren(widget: Widget): Widget[] {
    if (!widget.rows) {
      return []
    }

    const result: Widget[] = []
    const rows = widget.rows

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      const numWidgets = row.widgets.length

      for (let widgetIndex = 0; widgetIndex < numWidgets;widgetIndex++) {
        const widget = row.widgets[widgetIndex]

        result.push(widget)

        if (widget.rows) {
          result.concat(this.getNestedChildren(widget))
        }
      }
    }

    return result
  }

  getParentRow(widgetUUID: string, root?: WidgetContainer): WidgetRow | null {
    root = root || this.getContainer()

    const rows = root.rows
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      const numWidgets = row.widgets.length

      for (let widgetIndex = 0; widgetIndex < numWidgets;widgetIndex++) {
        const widget = row.widgets[widgetIndex]
        if (widget.wbUUID === widgetUUID) return row

        if (!widget.rows) continue

        const result = this.getParentRow(widgetUUID, widget as WidgetContainer)
        if (result !== null) return result
      }
    }

    return null
  }

  protected getRowColCount(row: WidgetRow) {
    const container = this.getRowContainer(row.wbUUID!)
    if (container === null) {
      console.error('PermutatorService.getRowColCount() - cannot find row container', 'row:', row)
    }

    return container!.size || 32
  }

  getRemainingColCount(row: WidgetRow) {
    if (!row) return 0

    let remainingCols = this.getRowColCount(row)
    row.widgets.forEach(widget => remainingCols -= widget.size || 0)
    return remainingCols
  }

  protected autoShrinkRow(row: WidgetRow, requiredSize: number): boolean {
    const proposedShrinks = this.getShrinkProposals(row, requiredSize)
    let numWidgetsShrinked = 0

    for (let rowUUID in proposedShrinks) {
      if (!proposedShrinks.hasOwnProperty(rowUUID)) {
        continue
      }

      if (!this.rowDataBeforeShrinkOperation.hasOwnProperty(rowUUID)) {
        this.rowDataBeforeShrinkOperation[rowUUID] = {}
      }

      const shrinks = proposedShrinks[rowUUID]
      const row = this.getByUUID(rowUUID) as WidgetRow

      shrinks.forEach(shrinkData => {
        numWidgetsShrinked++
        const shrinkWidget = row.widgets[shrinkData.itemindex]
        this.rowDataBeforeShrinkOperation[rowUUID][shrinkWidget.wbUUID] = shrinkWidget.size
        Vue.set(shrinkWidget, 'size', shrinkData.targetSize)
      })
    }

    return numWidgetsShrinked > 0
  }

  protected getContainerMaxShrinkSize(container: WidgetContainer) {
    let contentMinSize = 0

    container.rows.forEach(row => {
      const widgets = row.widgets
      const numWidgets = widgets.length
      let rowMinSize = 0
      let i = 0
      for (; i < numWidgets; i++) {
        const widget = widgets[i]

        // when we are evaluating the smallest possible size for a container to be shrinked to, it means, that the
        // placeholder will be located outside the container, in the same row as the container. therefore we need to
        // ignore it if we find it inside the container. it just hasn't been removed yet.
        if (widget.type === WidgetType.dragDropIndicator) {
          continue
        }

        // sum up the minsizes of each row's widget
        rowMinSize += widget.rows ? this.getContainerMaxShrinkSize(widget as WidgetContainer) : widget.minSize!
      }

      // find the largest row to define the content's required size
      if (rowMinSize > contentMinSize) {
        contentMinSize = rowMinSize
      }
    })

    if (contentMinSize < container.minSize!) {
      return container.minSize!
    }

    return contentMinSize
  }

  protected getShrinkProposals(row: WidgetRow, requiredSize: number, shrinkProposals?): ShrinkProposalList {
    const result: ShrinkProposalList = shrinkProposals || {}
    let requiredShrinkDistance = requiredSize - this.getRemainingColCount(row)

    // if no shrink is needed we have nothing to do
    if (requiredShrinkDistance <= 0) {
      return {}
    }

    const widgets = row.widgets
    const rowUUID = row.wbUUID!

    // go from right to left, because new widget is inserted on the right and it feels more natural if items
    // on the same side shrink down
    for (let itemindex = widgets.length - 1; itemindex >= 0; itemindex--) {
      const widget = widgets[itemindex]

      // no need to shrink the indicator
      if (widget.type === WidgetType.dragDropIndicator) {
        continue
      }

      const isContainer = widget.rows && (widget.rows.length > 0)
      const minSize = isContainer ? this.getContainerMaxShrinkSize(widget as WidgetContainer) : widget.minSize!
      let shrinkPotential = widget.size! - minSize

      // can we even shrink this widget?
      if (shrinkPotential <= 0) {
        continue
      }

      // more potential than actually needed?
      if (shrinkPotential > requiredShrinkDistance) {
        shrinkPotential = requiredShrinkDistance
      }

      if (!result[rowUUID]) {
        result[rowUUID] = []
      }

      // it's a shrinker!
      result[rowUUID].push({
        itemindex,
        targetSize: widget.size! - shrinkPotential
      })

      // if it's a container we have to store the shrink proposals for its contents as well
      if (isContainer) {
        // ts lint doesn't understand that the value of "isContainer" already contains the guard clause for widget.rows
        // @ts-ignore
        widget.rows.forEach(row => this.getShrinkProposals(row, shrinkPotential, result))
      }

      // decrease required distance
      requiredShrinkDistance -= shrinkPotential

      // are we done yet?
      if (requiredShrinkDistance <= 0) {
        return result
      }
    }

    // we return an empty shrink proposal list since at this point we weren't able to free up enough space
    // within the row
    return {}
  }

  addRowItem(row: WidgetRow, widget: Widget, targetIndex?: number): boolean {
    const minSize = widget.minSize || 1
    let requiredSize = widget.size || widget.defaultSize || minSize
    let remainingSize = this.getRemainingColCount(row)

    if (requiredSize > remainingSize) {
      requiredSize = (remainingSize > minSize) ? remainingSize : minSize
    }

    // if we hit this if-clause requiredSize is the widget's minimum size and it still doesn't fit in
    // let's see if we can shrink other widgets to free up some space for the new widget
    if (requiredSize > remainingSize) {
      if (!this.autoShrinkRow(row, requiredSize)) {
        if (this.errorState !== 'error-row-not-enough-space') {
          this.errorState = 'error-row-not-enough-space'
          EventService.$emit('error-row-not-enough-space', row, widget)
        }
        return false
      }
    }

    widget.size = requiredSize
    widget.sortindex = row.widgets.length
    row.widgets.push(widget)

    if (targetIndex !== undefined) {
      this.updateRowItemSortindex(row, row.widgets.length - 1, targetIndex)
    }

    return true
  }

  updateRowItemSortindex(row: WidgetRow, itemRowIndex, newSortIndex) {
    if (!row.widgets[itemRowIndex]) {
      return
    }

    newSortIndex = parseInt(newSortIndex, 10)

    let widget = row.widgets[itemRowIndex]
    const oldSortIndex = widget.sortindex!
    if (oldSortIndex === newSortIndex) {
      return
    }

    itemRowIndex = parseInt(itemRowIndex, 10)

    widget.sortindex = newSortIndex
    Vue.set(row.widgets, itemRowIndex, widget)

    let numItems = row.widgets.length
    for (let index = 0; index < numItems; index++) {
      // we manipulate the target item outside the loop
      if (index === itemRowIndex) {
        continue
      }

      widget = row.widgets[index]
      let sortindex = widget.sortindex!

      // is item moving down?
      if (newSortIndex < oldSortIndex) {
        // only modify sortindices within affected range
        if ((sortindex < newSortIndex) || (sortindex > oldSortIndex)) {
          continue
        }
        sortindex++
      }

      // is item moving up?
      if (newSortIndex > oldSortIndex) {
        // only modify sortindices within affected range
        if ((sortindex < oldSortIndex) || (sortindex > newSortIndex)) {
          continue
        }
        sortindex--
      }

      // assign sortindex and check bounds
      widget.sortindex = (sortindex < 0) ? 0 :
                         (sortindex >= numItems) ? numItems - 1 :
                         sortindex

      Vue.set(row.widgets, index, widget)
    }
  }

  protected createIndicatorWidget(template: Widget) {
    const indicatorWidget = WidgetFactory.create(WidgetType.dragDropIndicator)!

    indicatorWidget.wbUUID = this.uuid()
    indicatorWidget.minSize = ResizeService.getWidgetMinSize(template)
    indicatorWidget.maxSize = template.maxSize || 32
    indicatorWidget.size = template.size || template.defaultSize || indicatorWidget.minSize

    return indicatorWidget
  }

  rowContains(row: WidgetRow, widgetUUID: string): boolean {
    const widgets = row.widgets
    const numWidgets = widgets.length

    for (let i = 0; i < numWidgets; i++) {
      const rowWidget = widgets[i]

      if (rowWidget.wbUUID === widgetUUID) {
        return true
      }
    }

    return false
  }

  indicateDragDropOperation(rowUUID: string, widget: Widget, targetIndex: number) {
    const row = this.getByUUID(rowUUID) as WidgetRow | null
    if (!row) return

    const indicator = this.extractIndicatorFromRow(row)
    // is indicator already inside the row?
    if (indicator.widget) {
      this.updateRowItemSortindex(row, indicator.index, targetIndex)
      return
    }

    // if row contains the item being dragged replace it with the indicator
    if (this.rowContains(row, widget.wbUUID!)) {
      this.removeItemFromRow(row, widget.wbUUID!)
    }

    // if indicator could be added to the row remove it from previous rows
    if (this.addRowItem(row, this.createIndicatorWidget(widget), targetIndex)) {
      // is indicator currently inside a different row?
      if ((rowUUID !== this.rowContainingPlaceholderUUID) && this.rowContainingPlaceholderUUID) {
        this.removeDragDropIndicator(this.rowContainingPlaceholderUUID)
      }

      // store UUID of row containing the indicator
      this.rowContainingPlaceholderUUID = rowUUID
    }
  }

  removeItemFromRow(row: WidgetRow, widgetToRemoveUUID: string): boolean {
    const widgets = row.widgets
    let numWidgets = widgets.length
    let widgetToRemoveSortindex = -1
    let widgetFound = false

    for (let i = 0; i < numWidgets; i++) {
      let widget = widgets[i]
      if (widget.wbUUID !== widgetToRemoveUUID) {
        continue
      }

      widgetToRemoveSortindex = widget.sortindex!

      // remove widget from array
      row.widgets.splice(i, 1)
      numWidgets--

      widgetFound = true
      break
    }

    if (!widgetFound) {
      return false
    }

    for (let i = 0; i < numWidgets; i++) {
      let widget = widgets[i]
      let sortindex = widget.sortindex!

      if (sortindex <= widgetToRemoveSortindex) {
        continue
      }

      widget.sortindex = sortindex - 1

      Vue.set(row.widgets, i, widget)
    }

    return true
  }

  protected undoShrinkOperation(rowUUID: string) {
    if (!this.rowDataBeforeShrinkOperation.hasOwnProperty(rowUUID)) {
      return
    }

    const undoData = this.rowDataBeforeShrinkOperation[rowUUID]
    for (const widgetUUID in undoData) {
      if (!undoData.hasOwnProperty(widgetUUID)) {
        continue
      }

      const widget = this.getByUUID(widgetUUID) as Widget

      if (!widget) {
        continue
      }

      Vue.set(widget, 'size', undoData[widgetUUID])

      if (widget.rows) {
        widget.rows.forEach(row => this.undoShrinkOperation(row.wbUUID!))
      }
    }

    delete this.rowDataBeforeShrinkOperation[rowUUID]
  }

  removeDragDropIndicator(rowUUID?: string) {
    rowUUID = rowUUID || this.rowContainingPlaceholderUUID
    if (!rowUUID) return

    const row: WidgetRow = this.getByUUID(rowUUID) as WidgetRow
    if (!row) return

    const indicator = this.extractIndicatorFromRow(row)
    if (!indicator.widget) return

    this.removeItemFromRow(row, indicator.widget.wbUUID!)
    this.undoShrinkOperation(rowUUID)

    EventService.$emit('drag-drop-indicator-removed', rowUUID)
  }

  protected getDragOperationTarget(dropRowUUID: string): { row: WidgetRow, indicator: RowIndicatorDetails } | null {
    let row: WidgetRow = this.getByUUID(dropRowUUID) as WidgetRow
    if (!row) {
      row = this.getByUUID(this.rowContainingPlaceholderUUID) as WidgetRow
    }
    if (!row) return null

    let indicator = this.extractIndicatorFromRow(row)
    if (indicator.widget) {
      return {
        row: row,
        indicator: indicator
      }
    }

    if (row.wbUUID === this.rowContainingPlaceholderUUID) {
      return null
    }

    // if the row the item was dropped over doesn't have the necessary space to take up the item we return it
    // to the row the indicator is located at
    row = this.getByUUID(this.rowContainingPlaceholderUUID) as WidgetRow
    if (!row) return null

    indicator = this.extractIndicatorFromRow(row)
    if (!indicator.widget) return null

    return {
      row: row,
      indicator: indicator
    }
  }

  isRowEmpty(row: WidgetRow) {
    return row.widgets.length < 1
  }

  protected numEmptyRowsAtBottom(container: WidgetContainer) {
    const rows = container.rows
    const numRows = rows.length
    let numEmptyRowsAtBottom = 0
    let i = numRows - 1

    while ((i >= 0) && this.isRowEmpty(rows[i--])) {
      numEmptyRowsAtBottom++
    }

    return numEmptyRowsAtBottom
  }

  addEmptyRowIfNone(container?: WidgetContainer) {
    container = container || this.container!
    const numEmptyRowsAtBottom = this.numEmptyRowsAtBottom(container)
    if (numEmptyRowsAtBottom) return

    const row = WidgetRowFactory.createEmpty()
    row.wbUUID = this.uuid()

    container.rows.push(row)
  }

  prepareAllContainers(root?: WidgetContainer) {
    root = root || this.container!

    if (!root) {
      return
    }

    this.addEmptyRowIfNone(root)

    const rows = root.rows

    // look for other nested containers inside this container
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      const numWidgets = row.widgets.length

      for (let widgetIndex = 0; widgetIndex < numWidgets;widgetIndex++) {
        const widget = row.widgets[widgetIndex]

        if (widget.rows) {
          this.prepareAllContainers(widget as WidgetContainer)
        }
      }
    }
  }

  trimAllRows(keepSingleEmptyRow = false, root?: WidgetContainer) {
    root = root || this.container!

    this.trimRows(root)

    if (keepSingleEmptyRow) {
      this.addEmptyRowIfNone(root)
    }

    const rows = root.rows

    // look for other nested containers inside this container
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      const numWidgets = row.widgets.length

      for (let widgetIndex = 0; widgetIndex < numWidgets;widgetIndex++) {
        const widget = row.widgets[widgetIndex]

        if (widget.rows) {
          this.trimAllRows(keepSingleEmptyRow, widget as WidgetContainer)
        }
      }
    }
  }

  handleDeleteZoneDrop(row?: WidgetRow) {
    if (!row) {
      row = this.getByUUID(this.rowContainingPlaceholderUUID) as WidgetRow
      if (!row) {
        return
      }
    }

    const container = this.getRowContainer(row.wbUUID!)

    if (!container) {
      return
    }

    this.removeDragDropIndicator()
    this.trimRows(container)
    this.addEmptyRowIfNone(container)

    this.notifyAboutContainerUpdate()
  }

  trimRows(container?: WidgetContainer) {
    container = container || this.container!
    const numRowsToTrim = this.numEmptyRowsAtBottom(container)
    if (!numRowsToTrim) return

    container.rows.splice(-1 * numRowsToTrim, numRowsToTrim)
  }

  finishDragOperation(rowUUID: string, widget: Widget) {
    this.rowDataBeforeShrinkOperation = {}

    const target = this.getDragOperationTarget(rowUUID)
    if (target === null) return

    const indicator = target.indicator
    const row = target.row
    const indicatorWidget = indicator.widget!

    widget = JSON.parse(JSON.stringify(widget))
    widget.sortindex = indicatorWidget.sortindex
    widget.wbUUID = this.uuid()
    // copy size from indicator since item's might have been shrinked to free up space in the row
    widget.size = indicatorWidget.size

    // deep initialise container objects
    if (widget.isTemplate) {
      void this.executeWidgetDataInitializer(widget)
      if (widget.rows) {
        this.initialiseData(widget.rows)
      }
    }

    delete widget.isTemplate

    Vue.set(row.widgets, indicator.index, widget)

    const container = this.getRowContainer(row.wbUUID!) || undefined

    // if a user dragged out the last item of a row into a row above multiple empty rows can occur which we'll trim
    this.trimRows(container)

    // make sure there is always an empty row at the bottom
    this.addEmptyRowIfNone(container)

    // select widget in the settings area
    Vue.nextTick(() => EventService.$emit('widget-select-by-uuid', widget.wbUUID))

    this.notifyAboutContainerUpdate()
  }

  notifyAboutContainerUpdate() {
    EventService.$emit('permutator-service-container-update')
  }

  updateWidget(widgetUUID: string, widgetProps: Partial<Widget>) {
    const widget = this.getByUUID(widgetUUID) as Widget | null
    if (!widget) return

    for (const prop in widgetProps) {
      if (!widgetProps.hasOwnProperty(prop)) {
        continue
      }
      Vue.set(widget, prop, widgetProps[prop])
    }

    this.notifyAboutContainerUpdate()
  }

  getWidgetContainer(widgetUUID: string): WidgetContainer {
    const defaultContainer = this.getContainer()

    if (!widgetUUID) {
      return defaultContainer
    }

    const row = this.getParentRow(widgetUUID)!

    if (!row) {
      return defaultContainer
    }

    const container = this.getRowContainer(row.wbUUID!)

    if (!container) {
      return defaultContainer
    }

    return container
  }

  getByUUID(uuid: string): WidgetRow | Widget | null {
    return this._getByUUID(this.getContainer().rows, uuid)
  }

  getEmptyContainer(): WidgetContainer {
    const container = WidgetContainerFactory.create()
    container.wbUUID = this.uuid()
    return container
  }

  getContainer(): WidgetContainer {
    if (!this.container) {
      return this.getEmptyContainer()
    }

    return this.container
  }

  isRootContainer(containerUUID: string) {
    return containerUUID === this.rootContainerUUID
  }

  assign(container: WidgetContainer) {
    const rootContainerUUID = this.uuid()
    container.wbUUID = rootContainerUUID
    this.initialiseData(container.rows)
    this.container = container
    this.pristineData = JSON.parse(JSON.stringify(container))
    this.rootContainerUUID = rootContainerUUID
    this.trimAllRows()
  }

  getPristineState() {
    return JSON.parse(JSON.stringify(this.pristineData))
  }

  getRootContainerUUID() {
    return this.rootContainerUUID
  }

  getRowContainingPlaceholder(): WidgetRow | null {
    const rowUUID = this.rowContainingPlaceholderUUID

    if (!rowUUID) {
      return null
    }

    const row = this.getByUUID(rowUUID)

    if (!row) {
      return null
    }

    return row as WidgetRow
  }

  protected uninitialiseData(rows: WidgetRow[]) {
    rows.forEach(row => {
      delete row.wbUUID

      row.widgets.forEach(widget => {
        delete widget.wbUUID

        if (widget.rows) {
          this.uninitialiseData(widget.rows)
        }
      })
    })
  }

  dump(): string {
    const container = JSON.parse(JSON.stringify(this.container))
    delete container.wbUUID
    this.uninitialiseData(container.rows)
    this.trimAllRows(false, container)
    return JSON.stringify(container)
  }
}

export const PermutatorService = new PermutatorServiceProvider()
